import React from "react";
import { useTranslation } from "react-i18next";
import { Alert, Collapse } from "@mui/material";
import {
    isGroup,
    isLine,
    SaleOrdersFormTyping
} from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/types";
import { useFormContext, useWatch } from "react-hook-form";
import _ from "lodash";

function flattenLines(linesArray: SaleOrdersFormTyping["lines"] = []) {
    return linesArray.reduce((acc, item) => {
        if (isLine(item)) {
            acc.push(item);
        } else if (isGroup(item)) {
            acc.push(...item.lines);
        }
        return acc;
    }, [] as Exclude<SaleOrdersFormTyping["lines"][number], { name: string }>[]);
}

function LowStockWarningInner({ lines }: { lines: SaleOrdersFormTyping["lines"] }) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.orders" });

    const allLines = flattenLines(lines);
    const groupedLines = _.groupBy(allLines, line => line.product.id);

    const lowStockProducts = Object.values(groupedLines)
        .filter(group => {
            const product = group[0].product;
            if (!product || product.is_service) return false;
            const totalQuantity = _.sumBy(group, line => line.quantity || 0);
            return totalQuantity > (product.in_stock ?? 0);
        })
        .map(group => {
            const product = group[0].product;
            const totalQuantity = _.sumBy(group, line => line.quantity || 0);
            return {
                ...product,
                totalQuantity,
                inStock: product.in_stock ?? 0
            };
        });

    const isLowStock = lowStockProducts.length > 0;

    return (
        <Collapse in={isLowStock}>
            <Alert severity="warning">
                <div className="space-y-2">
                    <div>{t("lowStockWarning")}</div>
                    <div className="space-y-1">
                        {lowStockProducts.map(product => (
                            <div key={product.id}>
                                {product.code} {product.name} - {product.totalQuantity} / {product.inStock}
                            </div>
                        ))}
                    </div>
                </div>
            </Alert>
        </Collapse>
    );
}

const MemoizedLowStockWarning = React.memo(LowStockWarningInner, (prevProps, nextProps) =>
    _.isEqual(prevProps.lines, nextProps.lines)
);

export default function LowStockWarning() {
    const { control } = useFormContext<SaleOrdersFormTyping>();
    const lines = useWatch({ control, name: "lines" });

    return <MemoizedLowStockWarning lines={lines} />;
}